import {
  UploadAssetRequest,
  DeleteActivePeriods,
  DeleteCategory,
  DeleteProduct,
  DeleteSpecialPricePeriods,
  FetchSpecialPricePeriodsByProducts,
  SaveActivePeriods,
  SaveSpecialPricePeriods,
  DeleteBundle,
  FetchIntegrationBySlug,
  FetchOnlinePOSProductConnection,
  SaveOnlinePOSProductConnection,
  FetchSpecialOpeningHours,
} from "@greeter/venue-api";
import { EditBundleApiRequirements } from "./EditBundlePage";
import {
  ActivePeriod,
  ActivePeriodSchema,
  Bundle,
  BundleSchema,
  Category,
  CategoryCategory,
  CategoryCategorySchema,
  CategorySchema,
  ImageAsset,
  ImageSizeStrings,
  Product,
  Venue,
  VenueProduct,
  VenueProductSchema,
} from "@greeter/core";
import { ApiError } from "@greeter/api";
import { simulatedFetch } from "@greeter-venue/test-util";
import { readFileAsDataURL } from "@greeter-venue/utility";

function NotFoundError() {
  throw new ApiError({
    title: "Not found",
    detail:
      "The requested entity was not found. Please check if the id is correct",
    status: 404,
    data: {
      reasons: [],
      errorCode: "not-found",
    },
  });
}

type LocalStorageVenueApiOptions = {
  // Session id to uniquely store behind, is included in the key
  sessionId?: string;

  throttle?: "slow" | "regular" | "fast";
};

/**
 * A venue api implementation with persistent storage to test a "live" version of some feature.
 *
 * Does not reflect actual usage for production.
 */
export class LocalStorageVenueApi implements EditBundleApiRequirements {
  constructor(private opts?: LocalStorageVenueApiOptions) {
    this.seed();
  }
  fetchVenue(): Promise<Venue> {
    throw new Error("Method not implemented.");
  }
  fetchSpecialOpeningHours: FetchSpecialOpeningHours = () => {
    throw new Error();
  };

  fetchIntegrationBySlug: FetchIntegrationBySlug = () => {
    throw new Error();
  };

  saveOnlinePOSProductConnection: SaveOnlinePOSProductConnection = () => {
    throw new Error();
  };

  fetchOnlinePOSProductConnection: FetchOnlinePOSProductConnection = () => {
    throw new Error();
  };

  deleteBundle: DeleteBundle = () => {
    throw new Error();
  };

  saveActivePeriods: SaveActivePeriods = () => {
    throw new Error();
  };
  deleteActivePeriods: DeleteActivePeriods = () => {
    throw new Error();
  };
  saveSpecialPricePeriods: SaveSpecialPricePeriods = () => {
    throw new Error();
  };
  deleteSpecialPricePeriods: DeleteSpecialPricePeriods = () => {
    throw new Error();
  };
  deleteProduct: DeleteProduct = () => {
    throw new Error();
  };
  deleteCategory: DeleteCategory = () => {
    throw new Error();
  };
  fetchSpecialPricePeriodsByProducts: FetchSpecialPricePeriodsByProducts =
    () => {
      throw new Error();
    };

  private async seed() {
    console.group("[LocalStorageVenueApi][seed]");
    const seeded = Boolean(this.storage().getItem(this.key(`seeded`)));
    if (seeded) {
      console.debug("Already seeded, skipping...");
      console.groupEnd();
      return;
    }

    const categoryCategories: Array<CategoryCategory> = [];
    const ccSpirit = crypto.randomUUID();
    categoryCategories.push({
      id: ccSpirit,
      priority: 0,
      name: "Spiritus",
    });
    const ccShots = crypto.randomUUID();
    categoryCategories.push({ id: ccShots, priority: 0, name: "Shots" });
    const ccLuxury = crypto.randomUUID();
    categoryCategories.push({ id: ccLuxury, priority: 0, name: "Luksus" });
    const ccMixers = crypto.randomUUID();
    categoryCategories.push({ id: ccMixers, priority: 0, name: "Mixer" });
    this.storage().setItem(
      this.key("category-categories"),
      JSON.stringify(categoryCategories)
    );

    const categories: Map<string, Category> = new Map();
    const categoryId = crypto.randomUUID();
    categories.set(categoryId, {
      id: categoryId,
      name: "Vodka",
      categoryCategoryId: ccSpirit,
      priority: 0,
    });

    const shotsCategory = crypto.randomUUID();
    categories.set(shotsCategory, {
      id: shotsCategory,
      name: "Shots",
      categoryCategoryId: ccShots,
      priority: 0,
    });

    const rumCategory = crypto.randomUUID();
    categories.set(rumCategory, {
      id: rumCategory,
      name: "Rom",
      categoryCategoryId: ccSpirit,
      priority: 0,
    });

    const ginCategory = crypto.randomUUID();
    categories.set(ginCategory, {
      id: ginCategory,
      name: "Gin",
      categoryCategoryId: ccSpirit,
      priority: 0,
    });

    const whiskyCategory = crypto.randomUUID();
    categories.set(whiskyCategory, {
      id: whiskyCategory,
      name: "Whisky",
      categoryCategoryId: ccSpirit,
      priority: 0,
    });

    const tequilaCategory = crypto.randomUUID();
    categories.set(tequilaCategory, {
      id: tequilaCategory,
      name: "Tequila",
      categoryCategoryId: ccSpirit,
      priority: 0,
    });

    const sambucaCategory = crypto.randomUUID();
    categories.set(sambucaCategory, {
      id: sambucaCategory,
      name: "Sambuca",
      categoryCategoryId: ccShots,
      priority: 0,
    });

    const champagneCategory = crypto.randomUUID();
    categories.set(champagneCategory, {
      id: champagneCategory,
      name: "Champagne/Mousserende vin",
      categoryCategoryId: ccLuxury,
      priority: 0,
    });

    const liquorCategory = crypto.randomUUID();
    categories.set(liquorCategory, {
      id: liquorCategory,
      name: "Likør",
      categoryCategoryId: ccSpirit,
      priority: 0,
    });

    const rtdCategory = crypto.randomUUID();
    categories.set(rtdCategory, {
      id: rtdCategory,
      name: "RTD",
      categoryCategoryId: ccMixers,
      priority: 0,
    });

    const bitterCategory = crypto.randomUUID();
    categories.set(bitterCategory, {
      id: bitterCategory,
      name: "Bitter",
      categoryCategoryId: ccSpirit,
      priority: 0,
    });

    const mixerCategory = crypto.randomUUID();
    categories.set(mixerCategory, {
      id: mixerCategory,
      name: "Mixer",
      categoryCategoryId: ccMixers,
      priority: 0,
    });

    [...categories.values()].forEach(this.saveCategory.bind(this));

    const productId = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: productId,
      title: "0,7L Absolut Vodka",
      categories: [categoryId],
      cost: 30,
      price: 100,
    });

    const p2Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p2Id,
      title: "1,75L Absolut Vodka",
      categories: [categoryId],
      cost: 30,
      price: 100,
    });

    const p3Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p3Id,
      title: "3L Absolut Vodka",
      categories: [categoryId],
      cost: 30,
      price: 100,
    });

    const p4Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p4Id,
      title: "0,7L Absolut Vodka Elyx",
      categories: [categoryId],
      cost: 30,
      price: 100,
    });

    const p5Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p5Id,
      title: "1,75L Absolut Vodka Elyx",
      categories: [categoryId],
      cost: 30,
      price: 100,
    });

    const p6Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p6Id,
      title: "0,7L Absolut Vodka Raspberri",
      categories: [categoryId],
      cost: 30,
      price: 100,
    });

    const p7Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p7Id,
      title: "0,7L Belvedere Vodka",
      categories: [categoryId],
      cost: 30,
      price: 100,
    });

    const p8Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p8Id,
      title: "1,75L Belvedere Vodka",
      categories: [categoryId],
      cost: 30,
      price: 100,
    });

    const p9Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p9Id,
      title: "3L Belvedere Vodka",
      categories: [categoryId],
      cost: 30,
      price: 100,
    });

    const p10Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p10Id,
      title: "0,7L Havana Club Anejo 3 år",
      categories: [rumCategory],
      cost: 30,
      price: 100,
    });

    const p11Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p11Id,
      title: "0,7L Havana Club Anejo Especial",
      categories: [rumCategory],
      cost: 30,
      price: 100,
    });

    const p12Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p12Id,
      title: "0,7L Havana Club Anejo 7 år",
      categories: [rumCategory],
      cost: 30,
      price: 100,
    });

    const p13Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p13Id,
      title: "0,7L Malibu Coconut Rum",
      categories: [rumCategory],
      cost: 30,
      price: 100,
    });

    const p14Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p14Id,
      title: "0,7L Beefeater",
      categories: [ginCategory],
      cost: 30,
      price: 100,
    });

    const p15Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p15Id,
      title: "0,7L Hendricks",
      categories: [ginCategory],
      cost: 30,
      price: 100,
    });

    const p16Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p16Id,
      title: "0,7L Malfy Con Limone",
      categories: [ginCategory],
      cost: 30,
      price: 100,
    });

    const p17Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p17Id,
      title: "0,7L Malfy Con Limone",
      categories: [ginCategory],
      cost: 30,
      price: 100,
    });

    const p18Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p18Id,
      title: "0,7L Malfy Rosa",
      categories: [ginCategory],
      cost: 30,
      price: 100,
    });

    const p19Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p19Id,
      title: "0,5L Monkey47",
      categories: [ginCategory],
      cost: 30,
      price: 100,
    });

    const p20Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p20Id,
      title: "0,7L Malfy Con Limone",
      categories: [ginCategory],
      cost: 30,
      price: 100,
    });

    const p21Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p21Id,
      title: "0,7L Malfy Con Limone",
      categories: [rumCategory],
      cost: 30,
      price: 100,
    });

    const p22Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p22Id,
      title: "0,7L Jameson",
      categories: [whiskyCategory],
      cost: 30,
      price: 100,
    });

    const p23Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p23Id,
      title: "0,7L Jack Daniels Old No. 7",
      categories: [whiskyCategory],
      cost: 30,
      price: 100,
    });

    const p24Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p24Id,
      title: "0,7L Jack Daniels Old Apple",
      categories: [whiskyCategory],
      cost: 30,
      price: 100,
    });

    const p25Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p25Id,
      title: "0,7L Jack Daniels Old Honey",
      categories: [whiskyCategory],
      cost: 30,
      price: 100,
    });

    const p26Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p26Id,
      title: "0,7L Southern Comfort",
      categories: [whiskyCategory],
      cost: 30,
      price: 100,
    });

    const p27Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p27Id,
      title: "0,5L Minttu Pippermint",
      categories: [shotsCategory],
      cost: 30,
      price: 100,
    });

    const p28Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p28Id,
      title: "0,7L Pure Lakrids",
      categories: [shotsCategory],
      cost: 30,
      price: 100,
    });

    const p29Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p29Id,
      title: "0,7L Pure Æble",
      categories: [shotsCategory],
      cost: 30,
      price: 100,
    });

    const p30Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p30Id,
      title: "0,7L Råstoff Jordbær-Rabarber",
      categories: [shotsCategory],
      cost: 30,
      price: 100,
    });

    const p31Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p31Id,
      title: "0,7L Råstoff Pineapple-Vanilla",
      categories: [shotsCategory],
      cost: 30,
      price: 100,
    });

    const p32Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p32Id,
      title: "1L Små Bats",
      categories: [shotsCategory],
      cost: 30,
      price: 100,
    });

    const p33Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p33Id,
      title: "1L Små Fugle",
      categories: [shotsCategory],
      cost: 30,
      price: 100,
    });

    const p34Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p34Id,
      title: "1L Små Grønne",
      categories: [shotsCategory],
      cost: 30,
      price: 100,
    });

    const p35Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p35Id,
      title: "1L Små Sure Jordbær",
      categories: [shotsCategory],
      cost: 30,
      price: 100,
    });

    const p36Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p36Id,
      title: "1L Små Sure Juicy Mango",
      categories: [shotsCategory],
      cost: 30,
      price: 100,
    });

    const p37Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p37Id,
      title: "1L Små Sure Raspberry",
      categories: [shotsCategory],
      cost: 30,
      price: 100,
    });

    const p38Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p38Id,
      title: "1L Små Sure Æble",
      categories: [shotsCategory],
      cost: 30,
      price: 100,
    });

    const p39Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p39Id,
      title: "40x2cl Flügel Shots",
      categories: [shotsCategory],
      cost: 30,
      price: 100,
    });

    const p40Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p40Id,
      title: "0,7L Olmeca Tequila Blanco",
      categories: [tequilaCategory],
      cost: 30,
      price: 100,
    });

    const p41Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p41Id,
      title: "0,7L Licor 43",
      categories: [liquorCategory],
      cost: 30,
      price: 100,
    });

    const p42Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p42Id,
      title: "0,7L Passao",
      categories: [liquorCategory],
      cost: 30,
      price: 100,
    });

    const p43Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p43Id,
      title: "0,7L Pisang Ambon",
      categories: [liquorCategory],
      cost: 30,
      price: 100,
    });

    const p44Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p44Id,
      title: "0,7L Safari",
      categories: [liquorCategory],
      cost: 30,
      price: 100,
    });

    const p45Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p45Id,
      title: "0,7L Branca Menta",
      categories: [bitterCategory],
      cost: 30,
      price: 100,
    });

    const p46Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p46Id,
      title: "0,7L Jaegermeister",
      categories: [bitterCategory],
      cost: 30,
      price: 100,
    });

    const p47Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p47Id,
      title: "0,7L Sambuca Ramazzotti",
      categories: [sambucaCategory],
      cost: 30,
      price: 100,
    });

    const p48Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p48Id,
      title: "0,75L Moët & Chandon Champagne Nectar Impérial",
      categories: [champagneCategory],
      cost: 30,
      price: 100,
    });

    const p49Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p49Id,
      title: "0,75L Mumm Champagne Demi-Sec",
      categories: [champagneCategory],
      cost: 30,
      price: 100,
    });

    const p50Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p50Id,
      title: "0,75L Luc Belaire Rosé Fantome",
      categories: [champagneCategory],
      cost: 30,
      price: 100,
    });

    const p51Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p51Id,
      title: "0,75L Cava Nit Del Foc Brut",
      categories: [champagneCategory],
      cost: 30,
      price: 100,
    });

    const p52Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p52Id,
      title: "27,5CL Cult Shaker Spirt",
      categories: [rtdCategory],
      cost: 30,
      price: 100,
    });

    const p53Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p53Id,
      title: "27,5CL Smirnoff Ice Original",
      categories: [rtdCategory],
      cost: 30,
      price: 100,
    });

    const p54Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p54Id,
      title: "27,5CL Smirnoff Ice Raspberry",
      categories: [rtdCategory],
      cost: 30,
      price: 100,
    });

    const p55Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p55Id,
      title: "1L Ananas Juice",
      categories: [mixerCategory],
      cost: 30,
      price: 100,
    });

    const p56Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p56Id,
      title: "1L Tranebær Juice",
      categories: [mixerCategory],
      cost: 30,
      price: 100,
    });

    const p57Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p57Id,
      title: "1L Æble Juice",
      categories: [mixerCategory],
      cost: 30,
      price: 100,
    });

    const p58Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p58Id,
      title: "1L Coca Cola",
      categories: [mixerCategory],
      cost: 30,
      price: 100,
    });

    const p59Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p59Id,
      title: "1L Pepsi Max",
      categories: [mixerCategory],
      cost: 30,
      price: 100,
    });

    const p60Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p60Id,
      title: "1L Fanta",
      categories: [mixerCategory],
      cost: 30,
      price: 100,
    });

    const p61Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p61Id,
      title: "0,25CL Red Bull",
      categories: [mixerCategory],
      cost: 30,
      price: 100,
    });

    const p62Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p62Id,
      title: "0,25CL Red Bull Sugar Free",
      categories: [mixerCategory],
      cost: 30,
      price: 100,
    });

    const p63Id = crypto.randomUUID();
    this.saveProduct({
      ...Product.createDefault(),
      id: p63Id,
      title: "0,20CL Fever Tree Mediterranean",
      categories: [mixerCategory],
      cost: 30,
      price: 100,
    });

    this.storage().setItem(this.key("seeded"), `${true}`);
    console.groupEnd();
  }

  private storage(): typeof localStorage | typeof sessionStorage {
    // if (this.opts?.sessionId) {
    //   return sessionStorage;
    // }

    // return localStorage;
    return sessionStorage;
  }

  private key(path: string) {
    return `beta.LocalStorageVenueApi${
      this.opts?.sessionId ? `.${this.opts.sessionId}` : ""
    }.${path}`;
  }

  fetchBundle(id: string) {
    const item = this.storage().getItem(this.key(`bundles.${id}`));

    if (!item) {
      throw NotFoundError();
    }

    const parsed = BundleSchema.parse(JSON.parse(item));

    return simulatedFetch(parsed);
  }

  async fetchCategories(): Promise<Array<Category>> {
    const items = this.storage().getItem(this.key("categories"));

    if (!items) {
      return simulatedFetch([]);
    }

    const categories = JSON.parse(items).map(CategorySchema.parse);
    return categories;
  }

  async saveBundle(bundle: Bundle): Promise<Bundle> {
    try {
      console.group("[saveBundle]");
      this.storage().setItem(
        this.key(`bundles.${bundle.id}`),
        JSON.stringify(bundle)
      );

      return simulatedFetch(bundle);
    } catch (error) {
      console.error(
        "QuotaExceededError reached. Reset the session in order to continue!",
        error
      );
      alert(
        "TESTMODE: Browseren kan sq ikke holde på mere data. Det eneste du skal gøre er at åbne en ny fane, så burde du kunne uploade lige så tosset som du vil. Vær opmærksom på at billederne bliver gemt direkte i browseren, så hold dem små, det hober sig op hurtigt."
      );
      throw error;
    } finally {
      console.groupEnd();
    }
  }

  async fetchActivePeriods(bundleId: string): Promise<Array<ActivePeriod>> {
    const items = this.storage().getItem(
      this.key(`active-periods.${bundleId}`)
    );

    if (!items) return simulatedFetch([]);

    return simulatedFetch(JSON.parse(items).map(ActivePeriodSchema.parse));
  }

  async fetchProductsByCategory(
    categoryId: string
  ): Promise<Array<VenueProduct>> {
    const items = this.storage().getItem(
      this.key(`products.by-category.${categoryId}`)
    );

    if (!items) return simulatedFetch([]);

    return simulatedFetch(JSON.parse(items).map(VenueProductSchema.parse));
  }

  async fetchCategoryCategories(): Promise<Array<CategoryCategory>> {
    const items = this.storage().getItem(this.key(`category-categories`));

    if (!items) return simulatedFetch([]);

    return simulatedFetch(JSON.parse(items).map(CategoryCategorySchema.parse));
  }

  async fetchCategory(id: string) {
    const item = this.storage().getItem(this.key(`categories.${id}`));

    if (!item) {
      throw NotFoundError();
    }

    const parsed = JSON.parse(item);

    return simulatedFetch(CategorySchema.parse(parsed));
  }

  async saveCategory(category: Category) {
    console.group("[LocalStorageVenueApi][saveCategory]");
    this.storage().setItem(
      this.key(`categories.${category.id}`),
      JSON.stringify(category)
    );

    console.debug(
      "saved category",
      this.storage().getItem(this.key(`categories.${category.id}`))
    );

    const listKey = this.key("categories");
    const existingList = this.storage().getItem(listKey);

    if (existingList) {
      const parsed: Array<any> = JSON.parse(existingList);
      parsed.push(category);
      this.storage().setItem(listKey, JSON.stringify(parsed));
    } else {
      this.storage().setItem(listKey, JSON.stringify([category]));
    }

    console.groupEnd();
    return simulatedFetch<void>();
  }

  async uploadAsset(request: UploadAssetRequest): Promise<ImageAsset> {
    const b64: string = await readFileAsDataURL(request.data);
    console.log(
      "request.data.webkitRelativePath",
      request.data.webkitRelativePath
    );
    const imageAsset: ImageAsset = {
      id: crypto.randomUUID(),
      uri: "https://img.greeter.dk/gsF6d2LwT2h3KS9Cb8usTKrXFLf97_0QmlvOqEA9dos/rs:fill:512:385/g:ce/plain/https://greeterprodsa.blob.core.windows.net/assets/76dcab31-814c-41e5-92fe-4310476a2595@webp",
      name: request.data.name,
      path: "IM NOT REAL",
      root: "IM NOT REAL",
      sizes: ImageSizeStrings.map((size) => {
        return {
          uri: "https://img.greeter.dk/gsF6d2LwT2h3KS9Cb8usTKrXFLf97_0QmlvOqEA9dos/rs:fill:512:385/g:ce/plain/https://greeterprodsa.blob.core.windows.net/assets/76dcab31-814c-41e5-92fe-4310476a2595@webp",
          size,
        };
      }),
    };

    return simulatedFetch(imageAsset);
  }

  async fetchProduct(id: string) {
    const item = this.storage().getItem(this.key(`products.${id}`));

    if (!item) {
      throw NotFoundError();
    }

    return simulatedFetch(VenueProductSchema.parse(JSON.parse(item)));
  }

  async saveProduct(p: VenueProduct) {
    console.group("[LocalStorageVenueApi][saveProduct]");
    this.storage().setItem(this.key(`products.${p.id}`), JSON.stringify(p));

    console.debug("saving product", p);

    const productByCategoryKey = this.key(
      `products.by-category.${p.categories[0]}`
    );
    const items = this.storage().getItem(productByCategoryKey);

    if (items) {
      const products: Array<any> = JSON.parse(items);
      console.debug(
        `array already exists for category ${p.categories[0]}, adding to it`
      );
      products.push(p);
      this.storage().setItem(productByCategoryKey, JSON.stringify(products));
    } else {
      console.debug(
        `array does not exist for ${p.categories[0]}. Creating new array`
      );
      this.storage().setItem(productByCategoryKey, JSON.stringify([p]));
    }

    console.groupEnd();
    return simulatedFetch<void>();
  }
}
